import { router, useFocusEffect } from "expo-router";
import Cookies from "js-cookie";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { View, ActivityIndicator, Text, FlatList } from "react-native";
import Toast from "react-native-toast-message";
import { useDispatch } from "react-redux";

import Subtitle from "@/components/Subtitle";
import Title from "@/components/Title";
import Button from "@/components/button/Button";
import CardContact from "@/components/card/CardContact";
import InputSearch from "@/components/input/InputSearch";
import NonUsModal from "@/components/modal/banking/NonUsModal";
import { setForm } from "@/context/actions/actions";
import { sendRequest } from "@/utils/ApiUtil";
import { toastInstance } from "@/utils/ToastConfig";

export default function Contacts() {
  const [contacts, setContacts] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const nonUsModal = Cookies.get("nonUsModal");

  const page = 0;
  const pageSize = 100;
  const { t } = useTranslation();
  const dispatch = useDispatch();

  useFocusEffect(
    React.useCallback(() => {
      if (nonUsModal && nonUsModal === "true") setIsModalVisible(false);
      if (!nonUsModal) setIsModalVisible(true);
      loadContacts();
      setIsFetching(true);
      dispatch(
        setForm({ currentMenuItem: t("home/home:option.beneficiaries") })
      );
      return () => {
        setIsFetching(false);
      };
    }, [isFetching])
  );

  const fetchContacts = async (size: number) => {
    try {
      const request = await sendRequest(
        "get",
        "/counterparty",
        { page: size, pageSize },
        true
      );
      return request;
    } catch (error: any) {
      console.log(error);
      return [];
    }
  };

  const loadContacts = async () => {
    if (isFetching) return;
    let moreContacts = true;
    let currentPage = page;
    let contactsUpdated = false;
    const newContactsList = [...contacts];

    while (moreContacts) {
      const newContacts = await fetchContacts(currentPage);

      newContacts.forEach((newContact) => {
        const existingContactIndex = newContactsList.findIndex(
          (contact) => contact.id === newContact.id
        );

        if (existingContactIndex !== -1) {
          if (
            newContactsList[existingContactIndex].status !== newContact.status
          ) {
            newContactsList[existingContactIndex] = newContact;
            contactsUpdated = true;
          }
        } else {
          newContactsList.push(newContact);
          contactsUpdated = true;
        }
      });

      if (newContacts.length < pageSize) {
        moreContacts = false;
      } else {
        currentPage += 1;
      }
    }

    if (contactsUpdated) {
      setContacts(newContactsList);
    }

    setIsLoading(false);
  };

  const handlePress = (contact: any) => {
    if (contact.status === "ACTIVE") {
      if (contact.supportedRails.includes("ACH")) {
        contact.supportedRails = contact.supportedRails.filter(
          (rail: string) => rail !== "ACH"
        );
      }

      if (contact.supportedRails.length > 0) {
        dispatch(setForm({ currentCounterpartySelected: contact }));
        dispatch(
          setForm({
            rail: contact.supportedRails[0] ? contact.supportedRails[0] : "",
          })
        );

        if (
          !contact.accountInformation?.accountNumber &&
          contact.accountInformation?.iban
        ) {
          dispatch(setForm({ isIbanAccount: true }));
          dispatch(setForm({ isClabeAccount: false }));
        } else if (
          !contact.accountInformation?.iban &&
          contact.accountInformation?.accountNumber
        ) {
          dispatch(setForm({ isIbanAccount: false }));
          dispatch(setForm({ isClabeAccount: false }));
        } else if (contact?.accountInformation?.clabe) {
          dispatch(setForm({ isClabeAccount: true }));
          dispatch(setForm({ isIbanAccount: false }));
        }
        router.push("/home/transaction/amount");
      } else {
        toastInstance("info", t("common:toast.subtitle.norailsupported"));
      }
    } else {
      toastInstance(
        "info",
        t("common:toast.subtitle.beneficiaryunderanalysis")
      );
    }
  };

  const deleteCounterparty = async (id: string) => {
    try {
      const request = await sendRequest(
        "patch",
        "/counterparty",
        {
          counterpartyId: id,
          update: { status: "CLOSED" },
          reason: "Customer requested",
        },
        true
      );
      if (request) {
        Toast.show({
          type: "success",
          visibilityTime: 4000,
          props: {
            text1: t("common:toast.success"),
            text2: t("common:toast.subtitle.beneficiarydeleted"),
          },
        });
        setIsLoading(true);
        setIsFetching(false);
        setContacts([]);
        loadContacts();
      }
    } catch (error: any) {
      console.log(error);
    }
  };

  const renderContacts = (contacts) => {
    const renderItem = ({ item }) => {
      const accountNumber =
        item.accountInformation?.iban || item.accountInformation?.accountNumber;

      const routingNumber =
        item.accountInformation?.swiftBic ||
        item.accountInformation?.routingNumber;

      const rail = item?.supportedRails[0] ?? "";

      return (
        <CardContact
          key={item.id ? item.id : ""}
          id={item.id ? item.id : ""}
          nickname={item.profile?.name ?? ""}
          institutionName={item.accountInformation?.institutionName ?? ""}
          rail={rail}
          country={item.profile?.address?.countryCode ?? ""}
          status={item.status ?? ""}
          accountnumberiban={accountNumber ?? ""}
          routingnumberswiftbic={routingNumber ?? ""}
          data={item}
          handlePress={() => handlePress(item)}
          onAction={(action) => deleteCounterparty(action)}
        />
      );
    };

    return (
      <FlatList
        keyExtractor={(item) => item.id.toString()}
        contentContainerStyle={{
          height: 200,
          width: "100%",
          alignItems: "center",
        }}
        data={contacts}
        renderItem={renderItem}
      />
    );
  };

  const filteredContacts = contacts.filter(
    (contact) =>
      contact.profile.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <View className="grow bg-white1 p-4 lg:p-0">
      {isLoading && (
        <View className="absolute z-50 items-center justify-center h-full w-full bg-white1">
          <ActivityIndicator size="large" color="#5D0CBA" />
        </View>
      )}
      <View className="items-center justify-between w-full">
        <View className="m-2">
          <Title text={t("home/contacts:title")} />
        </View>
        <Subtitle text={t("home/contacts:subtitle")} />
        <View className="my-4">
          <InputSearch onSearchChange={(term) => setSearchQuery(term)} />
        </View>
        <View className="items-center w-full">
          <Button
            label={t("common:button.newbeneficiary")}
            onClick={() => router.push("/home/transaction/register")}
          />
        </View>
      </View>
      {contacts.length > 0 ? (
        filteredContacts.length > 0 ? (
          renderContacts(
            filteredContacts.filter((contact) => contact.status !== "CLOSED")
          )
        ) : (
          renderContacts(
            contacts.filter((contact) => contact.status !== "CLOSED")
          )
        )
      ) : (
        <View className="flex-grow w-full items-center">
          <Text className="text-center text-base font-normal">
            {t("common:placeholder.contactsempty")}
          </Text>
        </View>
      )}
      <NonUsModal
        visible={isModalVisible}
        onClose={() => {
          setIsModalVisible(false);
        }}
      />
    </View>
  );
}
